import {
  createContext,
  useContext,
  type InputHTMLAttributes,
  type ReactNode,
} from "react";
import { cn } from "@/lib/cn";
import styles from "./ui.module.css";

interface RadioGroupContextValue {
  name: string;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
}

const RadioGroupContext = createContext<RadioGroupContextValue | null>(null);

export interface RadioGroupProps {
  name: string;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  children: ReactNode;
  className?: string;
  legend?: ReactNode;
}

export function RadioGroup({
  name,
  value,
  onChange,
  disabled,
  children,
  className,
  legend,
}: RadioGroupProps) {
  return (
    <fieldset className={className} disabled={disabled}>
      {legend ? <legend className={styles.fieldLabel}>{legend}</legend> : null}
      <RadioGroupContext.Provider
        value={{
          name,
          value,
          onChange,
          ...(disabled !== undefined ? { disabled } : {}),
        }}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-8)" }}>
          {children}
        </div>
      </RadioGroupContext.Provider>
    </fieldset>
  );
}

export interface RadioProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, "type" | "onChange"> {
  value: string;
  label: ReactNode;
}

export function Radio({ value, label, className, ...rest }: RadioProps) {
  const ctx = useContext(RadioGroupContext);
  if (!ctx) {
    throw new Error("Radio must be used within RadioGroup");
  }
  return (
    <label className={cn(styles.radioRow, className)}>
      <input
        type="radio"
        name={ctx.name}
        value={value}
        checked={ctx.value === value}
        disabled={ctx.disabled || rest.disabled}
        onChange={() => ctx.onChange(value)}
        {...rest}
      />
      <span>{label}</span>
    </label>
  );
}
